import { Tip } from "./Tag";
import { createContext, useContext, useState } from "react";
import type { CSSProperties, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { useT } from "../../lib/i18n";
import { ApiTag, useLensOn } from "../apilens/ApiLens";
import { exactText, fmtFull, parseExactDraft, sameExact, type Money } from "../../lib/big";

type Align = "left" | "right";

/**
 * 控件的无障碍名称。DataTable 按列标题、Field 按字段标签（都只取字符串的）往下提供，
 * Input / Select / TextArea / CellInput 缺省拿它当 `aria-label`。
 *
 * 两处的输入框都没有和文字关联的 <label>：视觉上靠表头 / 左侧标签认，读屏在表单模式下只会念
 * 「编辑框」——实测亲密度页 33 个控件里 14 个没有名字。逐页补 aria-label 会漏，所以收在这一处。
 * 调用方显式传了 aria-label 的优先。
 */
export const ControlLabel = createContext<string | undefined>(undefined);

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "size"> {
  value: string | number | null | undefined;
  onChange?: (v: string) => void;
  align?: Align;
  size?: "sm" | "md";
}

export function Input({ value, onChange, align = "left", size = "md", disabled, className = "", ...rest }: InputProps) {
  /*
   * 宽度默认 w-full，但**调用方给了 w-* 就让位**。
   * 这里原先无条件带 w-full：ParamForm 明明传了 w-[130px]，两个类同时在
   * 同一层，实际渲染出来是 615~764px 的通栏输入框——一个填「4」的分钟数
   * 占了大半个面板，和设计稿完全不是一回事。类名冲突不报错，只能靠量。
   */
  const hasWidth = /(^|\s)w-/.test(className);
  // 同理：调用方给了 border-<色> 就别再输出默认色，两个类同层会打架
  const hasBorder = /(^|\s)border-(?!\[)(?!\d)[a-z]/.test(className);
  const cellLabel = useContext(ControlLabel);
  return (
    <input
      aria-label={cellLabel}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
      className={
        `${size === "sm" ? "h-control-sm text-xs" : "h-control text-base"} ${hasWidth ? "flex-none" : "w-full"} rounded-base border px-2 outline-none ` +
        `${align === "right" ? "text-right tabular-nums" : "text-left"} ` +
        // 两支末尾都要留空格：下面直接拼 className，少了就粘成 `cursor-not-allowedw-full`，调用方的宽度类失效、
        // 输入框退回默认宽，窄列里右对齐的数字被截掉看不见（2026-10-09 已发布版本总造价页的目标骰次列）
        (disabled
          ? "bg-fill-light text-ink-disabled border-line cursor-not-allowed "
          : `bg-white text-ink ${hasBorder ? "" : "border-line"} focus:border-primary placeholder:text-ink-placeholder `) +
        className
      }
      {...rest}
    />
  );
}

/**
 * 多行文本。样式与 Input 对齐，只是高度可长。
 *
 * idol 的个人介绍是长篇日文简介，塞进单行 Input 里只能看到十几个字，
 * 而多语言编辑要一次对照着填六种语言——单行框下这活没法干。
 */
export function TextArea({
  value, onChange, onBlur, disabled, placeholder, rows = 4, className = "",
}: {
  value: string;
  onChange?: (v: string) => void;
  /** 草稿型输入（打到一半的文本先留着、失焦再规范化）要它 */
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  className?: string;
}) {
  const cellLabel = useContext(ControlLabel);
  return (
    <textarea
      aria-label={cellLabel}
      value={value}
      rows={rows}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange?.(e.target.value)}
      className={
        `w-full resize-y rounded-base border px-2 py-1.5 text-base leading-relaxed outline-none ` +
        (disabled
          ? "bg-fill-light text-ink-disabled border-line cursor-not-allowed "
          : "bg-white text-ink border-line focus:border-primary placeholder:text-ink-placeholder ") +
        className
      }
    />
  );
}

/**
 * 原生下拉。选项少、不用搜索时用它（要搜索用 `SearchSelect`）。
 *
 * 存在的理由只有一个：**同一串类名原先在仓库里手抄了 15 份**，并且已经抄歪了
 * ——卡包的「解锁条件」那份漏了 `text-ink`，于是同一个抽屉里两个下拉的字色不一样。
 * 宽度/边框的让位规则与 `Input` 一致，两个控件并排时高度和圆角才对得上。
 */
export function Select({
  value, onChange, size = "md", disabled, className = "", children, ...rest
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value" | "size"> & {
  value: string | number;
  onChange?: (v: string) => void;
  size?: "sm" | "md";
}) {
  const hasWidth = /(^|\s)w-/.test(className);
  const hasBorder = /(^|\s)border-(?!\[)(?!\d)[a-z]/.test(className);
  const cellLabel = useContext(ControlLabel);
  return (
    <select
      aria-label={cellLabel}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
      className={
        `${size === "sm" ? "h-control-sm text-xs" : "h-control text-base"} ${hasWidth ? "flex-none" : "w-full"} rounded-base border px-2 outline-none ` +
        (disabled
          ? "bg-fill-light text-ink-disabled border-line cursor-not-allowed "
          : `bg-white text-ink ${hasBorder ? "" : "border-line"} focus:border-primary `) +
        className
      }
      {...rest}
    >
      {children}
    </select>
  );
}

/**
 * 数字输入。
 *
 * 值对外是 number，但**输入过程中必须按字符串留着**：直接把 number 回灌进
 * value，敲 "1." 时 Number("1.") === 1 会把小数点吃掉，接着敲 1 就变成 "11"
 * ——想输 1.1 输出来是 11，而且没有任何报错。所以聚焦期间显示本地草稿，
 * 失焦才交还给受控值。
 *
 * 中间态（""、"-"、"1."、"1.0"）一律放行，只挡真正的非数字字符。
 */
const NUM_DRAFT = /^-?\d*\.?\d*$/;

/** 「个数」类字段的中间态：只有数字，没有负号也没有小数点（见 CellInput 的 countOnly） */
const COUNT_DRAFT = /^\d*$/;

/** 草稿字符串 → 它代表的数值；中间态（空、"-"、"."）都代表「没有值」 */
const parseDraft = (raw: string): number | null => {
  if (raw === "" || raw === "-" || raw === "." || raw === "-.") return null;
  const n = Number(raw);
  // isFinite 而不是 !isNaN：粘贴几百位数字会得到 Infinity，
  // 它能一路混进 carryGap 这类校验并让「覆盖完整」误判通过
  return Number.isFinite(n) ? n : null;
};

/**
 * 清理粘贴进来的逗号。只有长得像千分位（1,234,567.8）才删逗号；
 * `1,5` 这种小数逗号写法直接拒收——静默把 0,25 变成 25 是十倍事故，
 * 让输入没反应，用户会自己改成 0.25。
 */
const THOUSANDS = /^-?\d{1,3}(,\d{3})+(\.\d*)?$/;
const stripCommas = (raw: string): string | null => {
  if (!raw.includes(",")) return raw;
  return THOUSANDS.test(raw) ? raw.replace(/,/g, "") : null;
};

type NumInputProps = Omit<InputProps, "onChange" | "value"> & (
  | { exact?: false; value: number | null; onChange?: (v: number | null) => void }
  /**
   * 精确整数：经济数值（造价、骰次、B、金币价）用它。只收非负整数，值可以超过 2^53，
   * 超出时以数字串进出（见 parseExactDraft），不会被 Number 舍入
   */
  | { exact: true; value: Money | bigint | null; onChange?: (v: Money | null) => void }
);

export function NumInput(props: NumInputProps) {
  if (props.exact) return <ExactNumInput {...props} />;
  return <DecimalNumInput {...props} />;
}

function ExactNumInput({
  value, onChange, align = "right", onBlur, exact: _exact, ...rest
}: Extract<NumInputProps, { exact: true }>) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft != null && sameExact(draft, value) ? draft : exactText(value);
  return (
    <Input
      value={shown}
      align={align}
      inputMode="numeric"
      onChange={(raw) => {
        const cleaned = stripCommas(raw);
        if (cleaned == null || !COUNT_DRAFT.test(cleaned)) return;
        setDraft(cleaned);
        onChange?.(parseExactDraft(cleaned));
      }}
      onBlur={(e) => {
        setDraft(null);
        onBlur?.(e);
      }}
      {...rest}
    />
  );
}

function DecimalNumInput({
  value, onChange, align = "right", onBlur, exact: _exact, ...rest
}: Extract<NumInputProps, { exact?: false }>) {
  const [draft, setDraft] = useState<string | null>(null);
  /*
   * 草稿只在「它还代表当前值」时有效。
   * 只写 draft ?? value 的话，外部把值改掉（点还原、切关卡、切版本）而输入框
   * 没失焦时，草稿会盖住新值——界面显示 1.1、实际数据是 3.1，脏标记还说没改动。
   * 用解析结果比对：这样 "1." "1.0" 这类中间态照样留得住。
   */
  const shown = draft != null && parseDraft(draft) === value ? draft : value ?? "";
  return (
    <Input
      value={shown}
      align={align}
      inputMode="decimal"
      onChange={(raw) => {
        const cleaned = stripCommas(raw);
        if (cleaned == null || !NUM_DRAFT.test(cleaned)) return;
        setDraft(cleaned);
        const n = parseDraft(cleaned);
        onChange?.(n);
      }}
      onBlur={(e) => {
        setDraft(null);
        onBlur?.(e);
      }}
      {...rest}
    />
  );
}

/** 只读值：浅灰底 + 虚线框，和输入框一眼可分。策划最常问的就是「这个能不能改」 */
export function ReadOnly({ children, align = "left", className = "" }: { children: ReactNode; align?: Align; className?: string }) {
  return (
    <div
      className={
        `h-control leading-[30px] rounded-base border border-dashed border-line-light bg-fill-light px-2 text-base text-ink-regular ` +
        `overflow-hidden text-ellipsis whitespace-nowrap ${align === "right" ? "text-right tabular-nums" : ""} ${className}`
      }
    >
      {children}
    </div>
  );
}

/**
 * 表单的标签列宽与列间距。**这是整个表单的属性，不是每一行各自的属性。**
 *
 * 之前八个表单各写各的标签宽：72 / 88 / 96 / 104 / 106 / 150 / 170px，
 * 并排看两个弹窗像两个产品。更麻烦的是「落在控件列」的东西要手算缩进
 * ——BatchGrid 的 `pl-[116px]`、RenameRival 的 `pl-[84px]`、ParamForm 的 `pl-[162px]`
 * 各自等于「某个标签宽 + 某个 gap」，改宽度就全错，而且错了不报错、只是歪掉。
 *
 * 用 CSS 变量下发，而不是 props 层层传：`Field` / `FieldNote` 各自按同一个变量
 * 排自己的两列，于是**行与行对齐是自动的**，`FieldNote` 的缩进也由同一个变量算出来
 * —— 全表单再没有一个需要人肉同步的数字。
 *
 * 变量都带默认值：不裹 `Form` 的 `Field` 照样是一行「标签 + 控件」，
 * 只是用默认列宽。这条很重要，它让这套东西可以一个弹窗一个弹窗地搬，
 * 而不是必须一次全改完。
 *
 * 112px 的来历：中文最长的字段名「可被万能卡兑换」91px，英文最长 115px
 * （已把 `pz.fld.reward` 的英文统一成和卡包一致的 "Set reward"），两边都放得下，
 * 又不至于给中文留出一大片空白——中文是主语言，标签右对齐，多出来的宽度全变成空隙。
 */
const LABEL_W = 112;
const COL_GAP = 12;

/** 一行两列时，两组「标签 + 控件」之间的间距 */
const PAIR_GAP = "gap-x-5";

export function Form({ children, labelWidth = LABEL_W, cols = 1, className = "" }: {
  children: ReactNode;
  /** 标签列宽（px）。字段名特别长的表单（参数表）才需要调 */
  labelWidth?: number;
  /** 每行放几组「标签 + 控件」，宽抽屉才用 2 */
  cols?: 1 | 2;
  className?: string;
}) {
  const vars = { "--label-w": `${labelWidth}px`, "--label-gap": `${COL_GAP}px` } as CSSProperties;
  return (
    <div
      style={vars}
      className={(cols === 2 ? `grid grid-cols-2 ${PAIR_GAP} gap-y-3` : "flex flex-col gap-3") + ` ${className}`}
    >
      {children}
    </div>
  );
}

/** 标签列 + 控件列，两处共用，保证 Field 与 FieldNote 的分栏完全一致 */
const ROW_COLS = "grid grid-cols-[var(--label-w,112px)_minmax(0,1fr)] gap-x-[var(--label-gap,12px)]";

/**
 * 一行「标签 + 控件」。
 *
 * `required` 的口径是**「不填就存不下去」**，不是「最好填一下」——
 * 星号是给运营的承诺：带星的填完，保存键一定能按。发布才需要的项不要带星，
 * 那属于发布闸，写在字段说明里。
 *
 * 标签一律 `pt-1.5` 顶对齐，不用 `items-center`：一行高的控件是 32px、
 * 标签行高 19.5px，6px 上边距刚好让它落在控件的光学中心；而多行控件
 * （多语言框、可增删的列表）要的正是「标签对齐第一行」。一条规则同时管住两种，
 * 原先是每个调用点自己在 `items-center` 和 `items-start + pt-1.5` 之间挑。
 */
export function Field({ label, hint, tip, required, api, children, stacked = false, labelExtra }: {
  label: ReactNode; hint?: ReactNode; tip?: string; required?: boolean;
  /** 标签行末尾的小入口（如参数说明视频的播放图标），跟在问号后面，不占行高 */
  labelExtra?: ReactNode;
  /** 紧凑参数区用上标签布局，其他表单保留标签列。 */
  stacked?: boolean;
  /** 接口视角的登记 id（src/api-lens）：这一行是哪个配置项。开关打开时标签下挂字段名，选取时认得出这一行 */
  api?: string;
  children: ReactNode;
}) {
  const lensOn = useLensOn();
  return (
    <div className={stacked ? "flex w-full min-w-0 flex-col gap-1" : ROW_COLS} data-api={api}>
      <div className={`flex ${api && lensOn ? "flex-wrap" : ""} items-center ${stacked ? "" : "justify-end pt-1.5 text-right"} gap-1 self-start text-sm text-ink-regular`}>
        <span>
          {label}
          {required && <span className="ml-0.5 text-danger" aria-hidden>*</span>}
        </span>
        {tip && <Hint text={tip} />}
        {labelExtra}
        {api && <ApiTag id={api} />}
      </div>
      {/* gap-x-2 由这里统一给：调用点原先各写 ml-2 / ml-3 / 什么都不写 */}
      <div className="flex min-h-control min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <ControlLabel.Provider value={typeof label === "string" ? label : undefined}>{children}</ControlLabel.Provider>
        {hint && <div className="w-full text-xs leading-relaxed text-ink-placeholder">{hint}</div>}
      </div>
    </div>
  );
}

/**
 * 落在控件列的整条内容：字段级的提示条、组尾说明、跨字段的校验话。
 * 和标签对不齐的说明会被读成「这是整个弹窗的事」，而它说的其实是上面那一格。
 *
 * `full` 连标签列一起占满，用于确实是「整个表单」的话。
 */
export function FieldNote({ full, className = "", children }: { full?: boolean; className?: string; children: ReactNode }) {
  if (full) return <div className={`col-span-2 min-w-0 ${className}`}>{children}</div>;
  return (
    <div className={`${ROW_COLS} col-span-2`}>
      <span aria-hidden />
      <div className={`min-w-0 ${className}`}>{children}</div>
    </div>
  );
}

export function Hint({ text, lines, tone }: { text?: string; lines?: string[]; tone?: "danger" }) {
  // danger 用「!」而不是「?」：它解释的是「为什么这个操作被挡住了」，
  // 和普通字段说明不是一回事，长得一样人不会去点。
  //
  // `lines` 用于「一次列出好几条」（一行拼图同时缺图又缺授权）。走 Tip 的多行分支
  // 而不是自己 join("\n")——HTML 里 \n 会被折成空格，几条问题会挤成一句话
  return (
    <Tip text={text} lines={lines}>
      <span
        className={
          "inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border text-[10px] leading-none " +
          (tone === "danger" ? "border-danger bg-danger-50 text-danger" : "border-line text-ink-placeholder")
        }
      >
        {tone === "danger" ? "!" : "?"}
      </span>
    </Tip>
  );
}

/**
 * 表格单元格里的数字输入。
 *
 * 失焦时显示千分位（4,300 好读），聚焦时切回裸数字（带逗号没法直接改）。
 * 设计稿里每个成本格都手写了这套 focus/blur 逻辑，这里收成一个。
 */
type CellInputProps = {
  disabled?: boolean;
  placeholder?: string;
  /** danger 用于标出待填格 */
  tone?: "danger";
  /**
   * 只收非负整数的字符：**`-` 和 `.` 一律不进来**（张数、价格、边长这类「个数」）。
   *
   * 这里划的界是**「什么字符永远不可能合法」**，不是「什么值不合法」——
   * 两者差别很大：`0` 和空串看着非法，却都是打 `10` 的必经中间态，
   * 在按键这一层拦它们等于让人打不出 10。所以键盘层只拦 `-` 和 `.`，
   * 「不能是 0 / 不能为空 / 不能超上限」交给值那一层当场标红。
   */
  countOnly?: boolean;
} & (
  | {
    exact?: false;
    value: number | null;
    /** 清空手动值后恢复该默认值；编辑过程保留空字符串和小数中间态。 */
    defaultValue?: number | null;
    onChange: (v: number | null) => void;
  }
  /**
   * 精确整数（经济数值：造价、星价、骰次）。隐含 countOnly；值可以超过 2^53，
   * 超出时以数字串进出，显示按 bigint 分组——不经 Number、不出现 "1e+21"
   */
  | {
    exact: true;
    value: Money | bigint | null;
    defaultValue?: Money | bigint | null;
    onChange: (v: Money | null) => void;
  }
);

export function CellInput(props: CellInputProps) {
  const { value, defaultValue, disabled, placeholder, tone } = props;
  const exact = props.exact === true;
  const countOnly = exact || props.countOnly;
  const t = useT();
  const ph = placeholder ?? t("common.pending");
  const [editing, setEditing] = useState(false);
  // 与 NumInput 同一个坑：编辑中必须留字符串，否则 "1." 会被 Number 吃掉小数点，
  // 想输 4.43 输出来是 443。B 值、系数这些格都是小数。
  const [draft, setDraft] = useState<string | null>(null);
  const cellLabel = useContext(ControlLabel);
  const displayed = value ?? defaultValue ?? null;
  const sameAsValue = (d: string) => (exact ? sameExact(d, value) : parseDraft(d) === value);
  const editValue = defaultValue !== undefined && draft != null
    ? draft
    : draft != null && sameAsValue(draft) ? draft : exact ? exactText(value) : (value as number | null) ?? "";
  // 失焦显示千分位：精确模式按 bigint 分组，number 的 toLocaleString 过了 2^53 末几位是错的
  const pretty = (v: typeof displayed) => (exact ? fmtFull(v) : (v as number).toLocaleString("en-US"));
  if (disabled) {
    return displayed == null ? (
      <span className={tone === "danger" ? "text-danger" : "text-ink-placeholder"}>{ph}</span>
    ) : (
      <span className="tabular-nums">{pretty(displayed)}</span>
    );
  }
  return (
    <input
      aria-label={cellLabel}
      value={editing ? editValue : displayed == null ? "" : pretty(displayed)}
      placeholder={ph}
      onFocus={() => { setEditing(true); if (defaultValue !== undefined) setDraft(displayed == null ? "" : exact ? exactText(displayed) : String(displayed)); }}
      onBlur={() => { setEditing(false); setDraft(null); }}
      onChange={(e) => {
        const raw = stripCommas(e.target.value);
        // 不合法的字符**直接不进来**（输入框没反应），不静默改写成别的值。
        // 与 stripCommas 拒收 `0,25` 同一个道理：静默纠正比没反应危险得多
        if (raw == null || !(countOnly ? COUNT_DRAFT : NUM_DRAFT).test(raw)) return;
        setDraft(raw);
        if (props.exact) props.onChange(parseExactDraft(raw));
        else props.onChange(parseDraft(raw));
      }}
      // tone 之前只作用在只读态的占位文字上，可编辑格里传了 danger 什么也不会发生
      // ——校验写了、界面上看不出来，比没校验更糟
      className={
        "h-7 w-full rounded-sm border bg-white px-1.5 text-right text-sm tabular-nums text-ink outline-none focus:ring-2 focus:ring-primary-100 " +
        (tone === "danger" ? "border-danger focus:border-danger" : "border-line focus:border-primary")
      }
    />
  );
}
